import React, { useState, useEffect } from 'react';
import { Building2, Plus, X, MapPin, Search } from 'lucide-react';
import { fetchHospitals, createHospital, fetchAllReps, assignPlannedVisits, fetchPlannedVisits } from '../services/api';

export default function HospitalManagement() {
  const [hospitals, setHospitals] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', address: '', city: '', latitude: '', longitude: '' });
  const [assignForm, setAssignForm] = useState({ repId: '', hospitalIds: [], startDate: '', endDate: '' });
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [plannedVisits, setPlannedVisits] = useState([]);
  const [pvDate, setPvDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [hResult, rResult] = await Promise.all([fetchHospitals(), fetchAllReps()]);
      if (hResult.success) setHospitals(hResult.data.hospitals || []);
      if (rResult.success) setReps(rResult.data.reps || []);
    } catch (err) {
      // Data load error handled silently
    } finally {
      setLoading(false);
    }
  };

  const loadPlannedVisits = async () => {
    try {
      const result = await fetchPlannedVisits({ date: pvDate });
      if (result.success) setPlannedVisits(result.data.plannedVisits || []);
    } catch (err) {
      // Planned visits load error handled silently
    }
  };

  useEffect(() => {
    if (pvDate) loadPlannedVisits();
  }, [pvDate]);

  const handleAddHospital = async () => {
    const { name, latitude, longitude } = addForm;
    if (!name || !latitude || !longitude) {
      alert('Name, latitude, and longitude are required');
      return;
    }
    setCreating(true);
    try {
      const result = await createHospital({
        ...addForm,
        latitude: parseFloat(addForm.latitude),
        longitude: parseFloat(addForm.longitude),
      });
      if (result.success) {
        setHospitals(prev => [...prev, result.data.hospital]);
        setShowAddModal(false);
        setAddForm({ name: '', address: '', city: '', latitude: '', longitude: '' });
      } else {
        alert(result.error || 'Failed to create hospital');
      }
    } catch (err) {
      alert('Failed to create hospital');
    } finally {
      setCreating(false);
    }
  };

  const handleAssign = async () => {
    const { repId, hospitalIds, startDate } = assignForm;
    if (!repId || hospitalIds.length === 0 || !startDate) {
      alert('Please select a rep, at least one hospital, and a start date');
      return;
    }
    setAssigning(true);
    try {
      const result = await assignPlannedVisits(assignForm);
      if (result.success) {
        alert(`Assigned ${result.data.hospitalCount} hospitals for ${result.data.datesAssigned} days`);
        setShowAssignModal(false);
        setAssignForm({ repId: '', hospitalIds: [], startDate: '', endDate: '' });
        loadPlannedVisits();
      } else {
        alert(result.error || 'Failed to assign visits');
      }
    } catch (err) {
      alert('Failed to assign visits');
    } finally {
      setAssigning(false);
    }
  };

  const toggleHospitalSelection = (id) => {
    setAssignForm(prev => ({
      ...prev,
      hospitalIds: prev.hospitalIds.includes(id)
        ? prev.hospitalIds.filter(h => h !== id)
        : [...prev.hospitalIds, id],
    }));
  };

  const filtered = hospitals.filter(h =>
    h.name.toLowerCase().includes(search.toLowerCase()) ||
    (h.city || '').toLowerCase().includes(search.toLowerCase())
  );

  // Group planned visits by rep
  const pvByRep = {};
  plannedVisits.forEach(pv => {
    if (!pvByRep[pv.rep_name]) pvByRep[pv.rep_name] = [];
    pvByRep[pv.rep_name].push(pv);
  });

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Hospital Management</h2>
            <p className="text-sm text-gray-500 mt-1">{hospitals.length} hospitals registered</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAssignModal(true)}
              className="bg-primary-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-800 transition flex items-center gap-1.5"
            >
              <MapPin size={16} /> Assign Visits
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition flex items-center gap-1.5"
            >
              <Plus size={16} /> Add Hospital
            </button>
          </div>
        </div>

        {/* Planned Visits for Date */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Planned Visits</h3>
            <input
              type="date"
              value={pvDate}
              onChange={e => setPvDate(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm"
            />
          </div>
          {Object.keys(pvByRep).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No planned visits for this date</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(pvByRep).map(([repName, visits]) => (
                <div key={repName} className="border rounded-lg p-3">
                  <p className="font-medium text-sm text-primary-900 mb-2">{repName}</p>
                  <div className="flex flex-wrap gap-2">
                    {visits.map(v => (
                      <span key={v.id} className="bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full font-medium">
                        {v.hospital_name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search hospitals..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm"
          />
        </div>

        {/* Hospital Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-900 mx-auto mb-3"></div>
            <p className="text-sm text-gray-500">Loading hospitals...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(h => (
              <div key={h.id} className="bg-white rounded-xl border p-4 hover:shadow-md transition">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <Building2 size={20} className="text-primary-700" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{h.name}</p>
                    {h.address && <p className="text-xs text-gray-500 truncate">{h.address}</p>}
                    <p className="text-xs text-gray-400 mt-1">
                      {h.city || 'N/A'} · {parseFloat(h.latitude).toFixed(4)}, {parseFloat(h.longitude).toFixed(4)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Hospital Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add Hospital</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <input type="text" placeholder="Hospital Name *" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              <input type="text" placeholder="Address" value={addForm.address} onChange={e => setAddForm(f => ({ ...f, address: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              <input type="text" placeholder="City" value={addForm.city} onChange={e => setAddForm(f => ({ ...f, city: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" step="any" placeholder="Latitude *" value={addForm.latitude} onChange={e => setAddForm(f => ({ ...f, latitude: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" />
                <input type="number" step="any" placeholder="Longitude *" value={addForm.longitude} onChange={e => setAddForm(f => ({ ...f, longitude: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
              <button onClick={handleAddHospital} disabled={creating} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
                {creating ? 'Creating...' : 'Add Hospital'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Visits Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg space-y-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Assign Planned Visits</h3>
              <button onClick={() => setShowAssignModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>

            <div className="space-y-4">
              {/* Select Rep */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Sales Rep</label>
                <select
                  value={assignForm.repId}
                  onChange={e => setAssignForm(f => ({ ...f, repId: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Select a rep...</option>
                  {reps.filter(r => r.role !== 'admin').map(r => (
                    <option key={r.id} value={r.id}>{r.name} ({r.town || 'No town'})</option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Start Date</label>
                  <input type="date" value={assignForm.startDate} onChange={e => setAssignForm(f => ({ ...f, startDate: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1">End Date</label>
                  <input type="date" value={assignForm.endDate} onChange={e => setAssignForm(f => ({ ...f, endDate: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>

              {/* Select Hospitals */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-2">
                  Hospitals ({assignForm.hospitalIds.length} selected)
                </label>
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {hospitals.map(h => (
                    <label key={h.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0">
                      <input
                        type="checkbox"
                        checked={assignForm.hospitalIds.includes(h.id)}
                        onChange={() => toggleHospitalSelection(h.id)}
                        className="rounded text-primary-600"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{h.name}</p>
                        <p className="text-xs text-gray-400">{h.city || h.address || ''}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowAssignModal(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
              <button onClick={handleAssign} disabled={assigning} className="bg-primary-900 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-800 disabled:opacity-50">
                {assigning ? 'Assigning...' : 'Assign Visits'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
